<template>
  <div class="row">
    <div class="col-md-5 col-md-offset-3">
      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsAlert v-if="from !== ''" class="tw-mb-6">
        <template #default>
          <template v-if="from === 'contact_invitation'">
            {{ $t('Welcome to Worksome!') }}
          </template>
          <template v-else-if="from === 'team_invitation'">
            {{ $t('Welcome to the team!') }}
          </template>
        </template>
        <template #description>
          <template v-if="from === 'contact_invitation'">
            {{ $t('You have now accepted the invitation') }}.
            <br />
          </template>
        </template>
      </WdsAlert>

      <WdsHeading as="h1" type="title1">
        {{ $t('Create a password for your account') }}
      </WdsHeading>

      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsText class="tw-mt-2 tw-mb-6" secondary>
        {{ $t('Add a new password to your account, and you will always be able to have access.') }}
      </WdsText>

      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsAlert v-if="canCreatePassword === false" error class="tw-mb-6">
        {{ $t('You cannot create a password.') }}
      </WdsAlert>

      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsAlert v-if="status !== ''" class="tw-mt-6">
        {{ status }}
      </WdsAlert>

      <WdsAlert class="tw-mb-6" info>
        <WdsText class="tw-mb-1">
          {{ $t('For a secure password, use 16 or more characters.') }}
        </WdsText>
        <WdsText>
          {{
            $t(
              'Shorter passwords (8–15 characters) must include at least one uppercase letter, one lowercase letter, and one number.',
            )
          }}
        </WdsText>
      </WdsAlert>

      <div class="tw-mt-6">
        <WdsInput
          v-model="password"
          name="password"
          type="password"
          :placeholder="$t('New password')"
          autocomplete="new-password"
          required
          :error="errors.password.length === 0 ? undefined : errors.password.join('\n')"
          @update:model-value="resetErrors"
        >
          <template #label>
            {{ $t('New password') }}
          </template>
        </WdsInput>

        <WdsInput
          v-model="passwordConfirmation"
          name="passwordConfirmation"
          type="password"
          :placeholder="$t('Confirm new password')"
          autocomplete="new-password"
          required
          :error="
            errors.passwordConfirmation.length === 0
              ? undefined
              : errors.passwordConfirmation.join('\n')
          "
          @update:model-value="resetErrors"
        >
          <template #label>
            {{ $t('Confirm new password') }}
          </template>
        </WdsInput>

        <div class="!tw-mt-[32px]">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <WdsCheckbox v-model:checked="acceptTerms" name="accept_terms" required>
            {{ $t("I agree to Worksome's") }}
            <a href="https://www.worksome.com/terms" target="_blank">{{ $t('Terms of Use') }}</a>
            {{ $t('and') }}
            <a href="https://www.worksome.com/privacy" target="_blank">
              {{ $t('Privacy Policy') }}
            </a>
          </WdsCheckbox>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <WdsText v-if="errors.acceptTerms.length !== 0" class="tw-mt-1">
            <span class="tw-text-red-500 tw-font-normal">
              {{ errors.acceptTerms.join('\n') }}
            </span>
          </WdsText>
        </div>

        <div class="text-center tw-mt-6">
          <WdsButton
            primary
            full-width
            :disabled="success || canCreatePassword === false"
            @click="changePassword"
          >
            {{ $t('Create password') }}
          </WdsButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import gql from 'graphql-tag'
import {
  WdsAlert,
  WdsButton,
  WdsCheckbox,
  WdsInput,
  WdsHeading,
  WdsText,
} from '@worksome/wds-components'
import { computed, Ref, ref, UnwrapRef } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'

const props = defineProps({
  from: {
    type: String,
    required: false,
    default: null,
  },
})

const password = ref(null)
const passwordConfirmation = ref(null)
const acceptTerms = ref(false)
const success = ref(false)
const status = ref('')
const errors: Ref<
  UnwrapRef<{ passwordConfirmation: string[]; password: string[]; acceptTerms: string[] }>
> = ref({
  password: [],
  passwordConfirmation: [],
  acceptTerms: [],
})

const { result } = useQuery(
  gql`
    query canCreatePassword {
      profile {
        canCreatePassword
      }
    }
  `,
  {},
  {
    clientId: 'publicApi',
  },
)
const canCreatePassword = computed(() => result.value?.profile?.canCreatePassword ?? null)

const { mutate: createPasswordMutation } = useMutation(
  gql`
    mutation createPassword($password: String!) {
      createPassword(input: { password: $password }) {
        id
      }
    }
  `,
  {
    clientId: 'publicApi',
  },
)

function resetErrors() {
  errors.value = {
    password: [],
    passwordConfirmation: [],
    acceptTerms: [],
  }
}
async function changePassword() {
  resetErrors()
  if (password.value !== passwordConfirmation.value) {
    errors.value.password.push($t('Password does not match.'))
    return
  }

  if (acceptTerms.value !== true) {
    errors.value.acceptTerms.push($t('Terms not accepted.'))
    return
  }

  try {
    await createPasswordMutation({
      password: password.value,
    })
    success.value = true
    status.value = $t('Your password has been created.')
  } catch (err: any) {
    err.graphQLErrors.forEach((error) => {
      errors.value.password = errors.value.password.concat(
        error.extensions.validation['input.password'] ?? error.extensions.validation.password ?? [],
      )
      errors.value.passwordConfirmation = errors.value.passwordConfirmation.concat(
        error.extensions.validation.passwordConfirmation ?? [],
      )
    })
  }
}
</script>

<style scoped></style>
